import React, {
	createContext,
	useState,
	useContext,
	useEffect,
	useCallback,
} from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { 
	saveHeaderMetricsSettings 
} from "../database/headerMetricsConfiguration";

// Default settings
const DEFAULT_SETTINGS = {
	enabled: true, // Enabled by default
	selectedMetrics: ["activeAnimals", "pregnancyRate", "farrowingRate"],
	refreshInterval: 0, // 0 means no auto-refresh or handled manually
};

// Available metrics definitions for reference/validation
export const AVAILABLE_METRICS = [
	{ id: "activeAnimals", label: "active-animals" },
	{ id: "liveBirthRate", label: "live-birth-rate" },
	{ id: "pregnancyRate", label: "pregnancy-rate" },
	{ id: "farrowingRate", label: "farrowing-rate" },
	{ id: "npd", label: "non-productive-days" },
	{ id: "avgLitterSize", label: "avg-litter-size" },
	{ id: "preWeaningMortality", label: "pre-weaning-mortality" },
];

const HeaderMetricsSettingsContext = createContext({
	settings: DEFAULT_SETTINGS,
	loading: true,
	updateSettings: async () => {},
	resetToDefaults: async () => {},
});

export const HeaderMetricsSettingsProvider = ({ children }) => {
	const { currentUser } = useAuth();
	const [settings, setSettings] = useState(DEFAULT_SETTINGS);
	const [loading, setLoading] = useState(true);

	// Load settings from Firestore
	useEffect(() => {
		if (!currentUser?.uid) {
			setLoading(false);
			return;
		}

		setLoading(true);
		
		const docRef = db
			.collection("Users")
			.doc(currentUser.uid)
			.collection("Account")
			.doc("HeaderMetricsSettings");

		const unsubscribe = docRef.onSnapshot(
			(doc) => {
				if (doc.exists) {
					const storedSettings = doc.data();
					setSettings((prev) => ({
						...prev,
						...storedSettings,
					}));
				}
				setLoading(false);
			},
			(error) => {
				console.error("[HeaderMetricsSettingsContext] Error listening to settings:", error);
				setLoading(false);
			}
		);

		return () => unsubscribe();
	}, [currentUser?.uid]);

	// Save settings to Firestore
	const updateSettings = useCallback(async (newSettings) => {
		if (!currentUser?.uid) return;

		// Optimistic update
		setSettings((prev) => {
			const updated = { ...prev, ...newSettings };
			return updated;
		});

		try {
			// We need the latest state, but since setState is async, 
			// we use the merged object we just created.
			// However, inside this callback, we should assume newSettings 
			// is the partial update. We need to merge with current 'settings' state, 
			// but we can't trust 'settings' in closure if it's stale.
			// Actually, let's just use the func form of setSettings to update state,
			// and we need to reconstruct what we are saving.
			
			// Better approach: Calculate the full new object
			// Let's rely on setSettings callback to get the definitive new state if we were doing complex logic,
			// but here we want to save *what we just set*.
			
			// Let's just merge with the current 'settings' from the outer scope which is a dependency.
			// But to avoid race conditions with multiple updates, it is safer to just send the merge payload to firestore 
			// if we are doing a merge set. The db function uses {merge: true}.
			
			await saveHeaderMetricsSettings(currentUser.uid, newSettings);
		} catch (error) {
			console.error("[HeaderMetricsSettingsContext] Error saving settings:", error);
			// Revert? For now, just log.
		}
	}, [currentUser?.uid]); // settings dependency removed as it's not used in the callback body

	const resetToDefaults = useCallback(async () => {
		if (!currentUser?.uid) return;
		setSettings(DEFAULT_SETTINGS);
		try {
			await saveHeaderMetricsSettings(currentUser.uid, DEFAULT_SETTINGS);
		} catch (error) {
			console.error("[HeaderMetricsSettingsContext] Error resetting settings:", error);
		}
	}, [currentUser?.uid]);

	return (
		<HeaderMetricsSettingsContext.Provider
			value={{
				settings,
				loading,
				updateSettings,
				resetToDefaults,
			}}
		>
			{children}
		</HeaderMetricsSettingsContext.Provider>
	);
};

export const useHeaderMetricsSettings = () => {
	const context = useContext(HeaderMetricsSettingsContext);
	if (!context) {
		throw new Error(
			"useHeaderMetricsSettings must be used within a HeaderMetricsSettingsProvider"
		);
	}
	return context;
};

export default HeaderMetricsSettingsContext;

